/**
 * DataBest — UserFormDrawer
 * Drop-in form for CrudPage that enforces role → scope_type coherence and
 * replaces the raw UUID input with a dependent dropdown of real entity names.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
    companiesApi,
    providersApi,
    groupHomesApi,
    housesApi,
} from "@/lib/orgApi";

const ROLE_OPTIONS = [
    { value: "system_admin", label: "System Admin" },
    { value: "company_admin", label: "Company Admin" },
    { value: "provider_admin", label: "Provider Admin" },
    { value: "supervisor", label: "Supervisor" },
    { value: "caregiver", label: "Caregiver" },
    { value: "analyst", label: "Analyst" },
];

// Role → allowed scope_types (first entry is the default).
const ROLE_SCOPE_MAP = {
    system_admin: ["global"],
    company_admin: ["company"],
    provider_admin: ["provider"],
    supervisor: ["group_home"],
    caregiver: ["house"],
    analyst: ["provider", "company", "group_home"],
};

const SCOPE_LABEL = {
    global: "Global",
    company: "Company",
    provider: "Provider",
    group_home: "Group Home",
    house: "House",
};

const SCOPE_API = {
    company: companiesApi,
    provider: providersApi,
    group_home: groupHomesApi,
    house: housesApi,
};

const scopeLabelFor = (row) => row.name || row.label || row.id;

const UserFormDrawer = ({
    mode,
    initialValues,
    saving,
    onSave,
    onClose,
    testidPrefix = "users",
}) => {
    const [form, setForm] = useState(() => ({
        email: initialValues.email || "",
        first_name: initialValues.first_name || "",
        last_name: initialValues.last_name || "",
        password: "",
        role: initialValues.role || "caregiver",
        scope_type:
            initialValues.scope_type ||
            ROLE_SCOPE_MAP[initialValues.role || "caregiver"][0],
        scope_id: initialValues.scope_id || "",
        active:
            typeof initialValues.active === "boolean"
                ? initialValues.active
                : true,
    }));

    const [scopeOptions, setScopeOptions] = useState([]);
    const [scopeLoading, setScopeLoading] = useState(false);

    const allowedScopeTypes = useMemo(
        () => ROLE_SCOPE_MAP[form.role] || ["global"],
        [form.role],
    );
    const scopeTypeLocked = allowedScopeTypes.length === 1;
    const needsScopeId = form.scope_type && form.scope_type !== "global";

    // Sync scope_type when role changes: if current scope_type is not allowed
    // for this role, reset to the default one (first in mapping).
    useEffect(() => {
        if (!allowedScopeTypes.includes(form.scope_type)) {
            const nextScope = allowedScopeTypes[0];
            setForm((prev) => ({
                ...prev,
                scope_type: nextScope,
                // Clear scope_id when scope_type changes to a different level.
                scope_id: "",
            }));
        }
    }, [form.role, form.scope_type, allowedScopeTypes]);

    // Load scope options whenever scope_type changes to a non-global level.
    useEffect(() => {
        let cancelled = false;
        const api = SCOPE_API[form.scope_type];
        if (!api) {
            setScopeOptions([]);
            return () => {
                cancelled = true;
            };
        }
        setScopeLoading(true);
        api
            .list()
            .then((rows) => {
                if (cancelled) return;
                const opts = (rows || []).map((r) => ({
                    value: r.id,
                    label: scopeLabelFor(r),
                }));
                setScopeOptions(opts);
            })
            .catch((err) => {
                if (cancelled) return;
                toast.error(
                    `Could not load ${SCOPE_LABEL[form.scope_type] || form.scope_type} list: ${
                        err?.message || "unknown error"
                    }`,
                );
                setScopeOptions([]);
            })
            .finally(() => {
                if (!cancelled) setScopeLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [form.scope_type]);

    const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    const submit = (e) => {
        e.preventDefault();
        if (needsScopeId && !form.scope_id) {
            toast.error(
                `Please pick a ${SCOPE_LABEL[form.scope_type]} for the Scope field.`,
            );
            return;
        }
        const payload = {
            email: form.email.trim().toLowerCase(),
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            role: form.role,
            scope_type: form.scope_type,
            scope_id: needsScopeId ? form.scope_id : null,
            active: !!form.active,
        };
        if (form.password) payload.password = form.password;
        onSave(payload);
    };

    const labelClass =
        "mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500";
    const inputClass =
        "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20";
    const selectClass = inputClass;
    const disabledClass =
        "cursor-not-allowed bg-slate-50 text-slate-500";

    return (
        <form
            onSubmit={submit}
            className="flex flex-1 flex-col overflow-hidden"
            data-testid={`${testidPrefix}-form`}
        >
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
                <div>
                    <label className={labelClass}>
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        data-testid={`${testidPrefix}-field-email`}
                        className={inputClass}
                    />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <label className={labelClass}>
                            First name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={form.first_name}
                            onChange={(e) =>
                                setField("first_name", e.target.value)
                            }
                            data-testid={`${testidPrefix}-field-first_name`}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>
                            Last name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={form.last_name}
                            onChange={(e) =>
                                setField("last_name", e.target.value)
                            }
                            data-testid={`${testidPrefix}-field-last_name`}
                            className={inputClass}
                        />
                    </div>
                </div>

                <div>
                    <label className={labelClass}>Password</label>
                    <input
                        type="password"
                        value={form.password}
                        onChange={(e) => setField("password", e.target.value)}
                        data-testid={`${testidPrefix}-field-password`}
                        className={inputClass}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                        Required on create. Leave empty on edit to keep the
                        current password.
                    </p>
                </div>

                <div>
                    <label className={labelClass}>
                        Role <span className="text-red-500">*</span>
                    </label>
                    <select
                        required
                        value={form.role}
                        onChange={(e) => setField("role", e.target.value)}
                        data-testid={`${testidPrefix}-field-role`}
                        className={selectClass}
                    >
                        {ROLE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className={labelClass}>
                        Scope type <span className="text-red-500">*</span>
                        {scopeTypeLocked && (
                            <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-slate-500">
                                Auto
                            </span>
                        )}
                    </label>
                    <select
                        required
                        value={form.scope_type}
                        onChange={(e) =>
                            setField("scope_type", e.target.value)
                        }
                        disabled={scopeTypeLocked}
                        data-testid={`${testidPrefix}-field-scope_type`}
                        className={`${selectClass} ${
                            scopeTypeLocked ? disabledClass : ""
                        }`}
                    >
                        {allowedScopeTypes.map((st) => (
                            <option key={st} value={st}>
                                {SCOPE_LABEL[st]}
                            </option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                        {scopeTypeLocked
                            ? "Determined automatically by the selected role."
                            : "Analysts can operate at Company, Provider or Group Home level."}
                    </p>
                </div>

                {needsScopeId && (
                    <div>
                        <label className={labelClass}>
                            Scope ({SCOPE_LABEL[form.scope_type]}){" "}
                            <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                required
                                value={form.scope_id || ""}
                                onChange={(e) =>
                                    setField("scope_id", e.target.value)
                                }
                                disabled={scopeLoading}
                                data-testid={`${testidPrefix}-field-scope_id`}
                                className={`${selectClass} ${
                                    scopeLoading ? "opacity-70" : ""
                                }`}
                            >
                                <option value="">
                                    {scopeLoading
                                        ? "Loading…"
                                        : `Select a ${SCOPE_LABEL[form.scope_type]}…`}
                                </option>
                                {scopeOptions.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                            {scopeLoading && (
                                <Loader2 className="pointer-events-none absolute right-9 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />
                            )}
                        </div>
                        {!scopeLoading && scopeOptions.length === 0 && (
                            <p className="mt-1 text-xs text-amber-700">
                                No {SCOPE_LABEL[form.scope_type]} records
                                available in your scope. Create one first.
                            </p>
                        )}
                    </div>
                )}

                <div>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={!!form.active}
                            onChange={(e) => setField("active", e.target.checked)}
                            data-testid={`${testidPrefix}-field-active`}
                            className="h-4 w-4 rounded border-slate-300 text-brand-blue-700 focus:ring-brand-blue-500"
                        />
                        Active
                    </label>
                </div>
            </div>

            <div className="shrink-0 flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
                <button
                    type="button"
                    onClick={onClose}
                    className="h-10 rounded-lg px-4 text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    data-testid={`${testidPrefix}-save-button`}
                    className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-blue-700 px-5 text-sm font-semibold text-white hover:bg-brand-blue-800 disabled:opacity-70"
                >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save
                </button>
            </div>
        </form>
    );
};

export default UserFormDrawer;
